import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, Divider,
  FormControlLabel, IconButton, Slider, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import { getFontEmbedCSS, toPng } from 'html-to-image';
import { useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import AppMockup, { MockupSettings, PlateSettings, defaultSettings } from './AppMockup';
import { artFileAsDataUri, saveDataUrl } from '../lib/download';
import { AppArt, AppArtFile, artRecipeOf } from '../types';

/** As duas telas da captura, com as imagens já embutidas. */
interface Embedded {
  welcome: string | null;
  header: string | null;
  logo: string | null;
}

/**
 * As webfonts do documento embutidas na captura, resolvidas UMA vez por sessão.
 *
 * O `html-to-image` embute fonte a cada captura, e no nosso caso esse caminho é
 * caro e sujo: as folhas do Google Fonts entram no `index.html` sem
 * `crossorigin`, então ler o `cssRules` delas estoura por segurança e a lib cai
 * no plano B — rebaixar cada arquivo de fonte de novo (esse caminho não passa
 * pelo cache dela) e INSERIR as regras resultantes, já com o base64 dentro, num
 * stylesheet vivo do documento. As regras nunca saem de lá: cada captura deixa
 * o admin um pouco mais lento e engorda a captura seguinte.
 *
 * Resolvendo uma vez e passando pronto, nada disso roda de novo. O recorte é
 * por fonte USADA no nó, e como aqui o nó é sempre a mesma folha do mockup, o
 * primeiro cálculo serve para os demais.
 */
let embeddedFontCss: Promise<string> | null = null;
const fontCssFor = (node: HTMLElement) => {
  if (!embeddedFontCss) { embeddedFontCss = getFontEmbedCSS(node).catch(() => ''); }
  return embeddedFontCss;
};

const slug = (text: string) => text.toLowerCase().normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'rede';

/**
 * O mockup ao vivo: as telas do app montadas no admin, com a arte gerada atrás
 * e o chrome ajustável na hora.
 *
 * O que se decide aqui não dá para decidir olhando o PNG: se o texto branco
 * some no céu claro da fachada, se a logo precisa de um prato para aparecer,
 * se vale desligar o BEM-VINDO! porque a arte já grava a identidade. Mexer nos
 * controles muda só a visualização — o que persiste é o que você escolher
 * aplicar no rascunho da rede.
 */

/** Entrada de cor solta (sem form), com o seletor nativo do browser. */
const ColorField = ({ label, value, onChange }: {
  label: string; value: string; onChange: (hex: string) => void;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box
      component="input"
      type="color"
      value={value}
      onChange={event => onChange((event.target as HTMLInputElement).value.toUpperCase())}
      sx={{
        width: 32, height: 32, padding: 0, border: '1px solid', borderColor: 'divider',
        borderRadius: 1, cursor: 'pointer', backgroundColor: 'transparent', flex: 'none',
      }}
    />
    <Box>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="caption" color="text.secondary">{value}</Typography>
    </Box>
  </Box>
);

/** Prato atrás da logo: cor + quanto ele cobre. Em 0% a logo fica solta na arte. */
const PlateField = ({ label, plate, onChange }: {
  label: string; plate: PlateSettings; onChange: (next: PlateSettings) => void;
}) => (
  <Box>
    <ColorField label={label} value={plate.color} onChange={color => onChange({ ...plate, color })} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, pl: '40px' }}>
      <Slider
        size="small"
        value={plate.opacity}
        min={0}
        max={1}
        step={0.05}
        onChange={(_, value) => onChange({ ...plate, opacity: value as number })}
        sx={{ maxWidth: 150 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 78 }}>
        {plate.opacity === 0 ? 'sem fundo' : `${Math.round(plate.opacity * 100)}% opaco`}
      </Typography>
    </Box>
  </Box>
);

interface Props {
  open: boolean;
  onClose: () => void;
  record: AppArt;
  /** Artboards do modelo em foco; ausentes, o fundo é o campo da marca. */
  welcomeArt: AppArtFile | null;
  headerArt: AppArtFile | null;
  /** Rótulo do modelo, para o cabeçalho do modal. */
  title?: string;
  /** A FOLHA do modelo. É dela que vem a receita da geração. */
  model?: AppArtFile | null;
  /** Só o rascunho aceita gravar as cores escolhidas aqui. */
  editable?: boolean;
}

const MockupDialog = ({ open, onClose, record, welcomeArt, headerArt, title, model, editable }: Props) => {
  const notify = useNotify();
  const form = useFormContext();
  const [screen, setScreen] = useState<'welcome' | 'home'>('welcome');
  const recipe = artRecipeOf(model);
  const [settings, setSettings] = useState<MockupSettings>(() => defaultSettings(record, recipe));
  const [embedded, setEmbedded] = useState<Embedded | null>(null);
  const [capturing, setCapturing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Reabrir com outro modelo (ou depois de mudar as cores no form) recomeça do
  // que está gravado — senão o modal mostra o ajuste de outra sessão.
  useEffect(() => {
    if (open) { setSettings(defaultSettings(record, recipe)); }
  }, [open, record, recipe]);

  const logo = (record.files || [])
    .filter(file => file.bucket === 'logo' as any)
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))[0];

  const art = screen === 'welcome' ? welcomeArt : headerArt;
  const set = (patch: Partial<MockupSettings>) => setSettings(current => ({ ...current, ...patch }));

  /**
   * Captura as DUAS telas num PNG só — o que se manda para o cliente.
   *
   * Em dois passos por necessidade: primeiro as imagens viram data URI (canvas
   * com imagem de outra origem fica "sujo" e não rasteriza), o que só entra na
   * árvore no render seguinte; o efeito abaixo é quem tira a foto quando esse
   * render acontece.
   */
  const capture = async () => {
    setCapturing(true);
    try {
      const [welcome, header, logoUri] = await Promise.all([
        welcomeArt ? artFileAsDataUri(welcomeArt) : null,
        headerArt ? artFileAsDataUri(headerArt) : null,
        logo ? artFileAsDataUri(logo) : null,
      ]);
      setEmbedded({ welcome, header, logo: logoUri });
    } catch (error: any) {
      notify(error?.message || 'Não foi possível preparar a captura', { type: 'error' });
      setCapturing(false);
    }
  };

  useEffect(() => {
    if (!embedded || !sheetRef.current) { return; }
    const node = sheetRef.current;

    (async () => {
      try {
        // Sem esperar fonte e imagem, a foto sai com fallback de fonte e quadro
        // vazio no lugar da arte.
        await document.fonts.ready;
        await Promise.all([...node.querySelectorAll('img')]
          .map(img => img.decode().catch(() => undefined)));

        const dataUrl = await toPng(node, {
          pixelRatio: 2, backgroundColor: '#14161a', fontEmbedCSS: await fontCssFor(node),
        });
        await saveDataUrl(dataUrl, `${slug(record.chainName)}-${slug(title || 'mockup')}.png`);
      } catch (error: any) {
        notify(error?.message || 'Não foi possível gerar a captura', { type: 'error' });
      } finally {
        setEmbedded(null);
        setCapturing(false);
      }
    })();
  }, [embedded, notify, record.chainName, title]);

  const applyColors = () => {
    form.setValue('color', settings.brand, { shouldDirty: true });
    form.setValue('headerLabelColor', settings.ink, { shouldDirty: true });
    form.setValue('signUpButtonColor', settings.signUp, { shouldDirty: true });
    form.setValue('signInButtonColor', settings.signIn, { shouldDirty: true });
    notify('Cores aplicadas no rascunho — salve para gravar.', { type: 'info' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ backgroundColor: 'grey.900', p: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', minHeight: 640 }}>

          {/* Palco */}
          <Box sx={{
            flex: '1 1 420px', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', py: 3, gap: 2,
          }}>
            <Tabs
              value={screen}
              onChange={(_, value) => setScreen(value)}
              textColor="inherit"
              sx={{ '& .MuiTab-root': { color: 'grey.400' }, '& .Mui-selected': { color: '#fff' } }}
            >
              <Tab value="welcome" label="Boas-vindas" />
              <Tab value="home" label="Home" />
            </Tabs>

            <AppMockup
              screen={screen}
              art={art ? (art.previewUrl || art.fileUrl) : null}
              logo={logo ? (logo.previewUrl || logo.fileUrl) : null}
              name={record.appNameInside || record.appName || record.chainName}
              slogan={record.slogan || ''}
              settings={settings}
              width={300}
            />

            <Typography variant="caption" sx={{ color: 'grey.500' }}>
              {art
                ? `${screen === 'welcome' ? 'chain-background.png' : 'chain-header-background.png'} · ${art.width}×${art.height}`
                : 'sem artboard nesta rodada — fundo é o campo da marca'}
            </Typography>
          </Box>

          {/* Controles */}
          <Box sx={{
            flex: '0 0 340px', backgroundColor: 'background.paper', p: 2.5,
            display: 'flex', flexDirection: 'column', gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {title || 'Ver no app'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {record.chainName}
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
            </Box>

            {/* Dizer de onde vieram os valores: sem isto, quem abre não sabe se a
                tela chutou ou se está mostrando a decisão do gerador — e mexeria
                nos controles achando que precisa configurar tudo do zero. */}
            {!!recipe && (
              <Typography variant="caption" color="text.secondary">
                Aberto com a receita do modelo{recipe.nome ? <> — <strong>{recipe.nome}</strong></> : null}:
                cor do texto e dos botões, largura da logo e as boas-vindas já vêm dela.
                Mexa à vontade; <strong>Restaurar</strong> volta para esses valores.
              </Typography>
            )}

            {!welcomeArt && !headerArt && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Esta rodada não tem os artboards. Gere de novo com o gerador
                atualizado — ele passa a subir o welcome e o header junto da folha.
              </Alert>
            )}

            <Divider />

            <Typography variant="overline" color="text.secondary">Cores</Typography>
            {recipe && recipe.cores?.gravadas?.paleta !== 'marca' && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                Esta arte foi gerada na paleta <strong>{recipe.cores.gravadas.paleta}</strong>: o
                fundo já está rasterizado em {recipe.cores.gravadas.brand} com acento{' '}
                {recipe.cores.gravadas.brand2}. Mudar a cor principal aqui muda o chrome que o app
                desenha, <strong>não</strong> o fundo — para o fundo acompanhar, é preciso gerar
                de novo.
              </Alert>
            )}
            <ColorField label="Cor principal" value={settings.brand} onChange={brand => set({ brand })} />
            <ColorField label="Texto do header (home)" value={settings.ink} onChange={ink => set({ ink })} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              É o <code>appHeaderLabelColor</code>: vale no header da home (nome, slogan,
              sino, saldo e a borda do Extrato). Nas boas-vindas o app escreve
              sempre em branco, cravado no código — não há configuração.
            </Typography>
            <ColorField label="Botão de cadastro" value={settings.signUp} onChange={signUp => set({ signUp })} />
            <ColorField label="Botão de login" value={settings.signIn} onChange={signIn => set({ signIn })} />

            <Divider />

            <Typography variant="overline" color="text.secondary">O que o app desenha</Typography>
            {!!recipe?.app?.gravadoNoAsset?.length && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                O asset já traz {recipe.app.gravadoNoAsset.join(' e ')} desenhado. Por isso as
                boas-vindas do app entram desligadas — ligá-las duplica o que o PNG já tem.
              </Alert>
            )}
            <Tooltip title="appShowWelcomeLabel: no app é uma prop só e é tudo-ou-nada — BEM-VINDO!, logo, nome e slogan aparecem ou somem juntos. Desligue quando a arte já grava a identidade (modelo que grava logo ou logo + manchete).">
              <FormControlLabel
                control={<Checkbox checked={settings.showWelcomeLabel} onChange={(_, v) => set({ showWelcomeLabel: v })} />}
                label="Boas-vindas: BEM-VINDO!, logo, nome e slogan"
              />
            </Tooltip>
            <FormControlLabel
              control={<Checkbox checked={settings.showHeaderLogo} onChange={(_, v) => set({ showHeaderLogo: v })} />}
              label="Home: logo no header"
            />

            {(settings.showWelcomeLabel || settings.showHeaderLogo) && (
              <>
                <Divider />
                <Typography variant="overline" color="text.secondary">Fundo atrás da logo</Typography>
                <PlateField
                  label="Nas boas-vindas"
                  plate={settings.welcomePlate}
                  onChange={welcomePlate => set({ welcomePlate })}
                />
                <PlateField
                  label="No header da home"
                  plate={settings.headerPlate}
                  onChange={headerPlate => set({ headerPlate })}
                />
                <Typography variant="caption" color="text.secondary">
                  Serve para logo que some na arte. Em 0% ela fica solta, com a
                  transparência do arquivo.
                </Typography>
              </>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* A ação principal ocupa a linha inteira: no painel de 340px, três
                botões lado a lado quebravam o rótulo em duas linhas, e o
                `<span>` que o Tooltip exige desalinhava a base dos outros. */}
            <Stack spacing={1}>
              {editable && (
                <Tooltip title="Grava as quatro cores nos campos do rascunho. O resto (logo, label, fundo) é só visualização.">
                  <Button variant="contained" fullWidth onClick={applyColors} sx={{ whiteSpace: 'nowrap' }}>
                    Aplicar cores
                  </Button>
                </Tooltip>
              )}

              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Button
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => setSettings(defaultSettings(record, recipe))}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Restaurar
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Gera um PNG com as duas telas, do jeito que estão ajustadas aqui — é o que se manda para o cliente.">
                  <Button
                    size="small"
                    startIcon={capturing ? <CircularProgress size={14} /> : <CameraAltOutlinedIcon />}
                    onClick={capture}
                    disabled={capturing}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {capturing ? 'Gerando…' : 'Captura'}
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Fora da tela, em tamanho cheio: a captura não pode herdar a redução
            do palco nem o recorte do modal. */}
        {embedded && (
          // O off-screen fica no PAI de propósito. A lib clona o nó COM o estilo
          // dele: se o próprio nó fotografado fosse `fixed` a -20000px, o clone
          // continuaria fora do quadro do SVG e o PNG saía só com o fundo — que
          // foi exatamente o bug da primeira versão.
          <Box sx={{ position: 'fixed', left: -20000, top: 0 }}>
          <Box
            ref={sheetRef}
            sx={{
              display: 'inline-flex', gap: 3, alignItems: 'flex-start',
              p: 3, backgroundColor: '#14161a',
            }}
          >
            {(['welcome', 'home'] as const).map(which => (
              <Box key={which}>
                <AppMockup
                  screen={which}
                  art={which === 'welcome' ? embedded.welcome : embedded.header}
                  logo={embedded.logo}
                  name={record.appNameInside || record.appName || record.chainName}
                  slogan={record.slogan || ''}
                  settings={settings}
                />
                <Box sx={{ mt: 1.5, color: '#8b93a1', fontSize: 13 }}>
                  {which === 'welcome' ? 'Boas-vindas' : 'Home'}
                </Box>
              </Box>
            ))}
            <Box sx={{ color: '#fff', maxWidth: 220, pt: 1 }}>
              <Box sx={{ fontSize: 20, fontWeight: 700 }}>{title || 'Ver no app'}</Box>
              <Box sx={{ fontSize: 14, color: '#8b93a1', mt: 0.5 }}>{record.chainName}</Box>
            </Box>
          </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MockupDialog;
